import { useContext, useEffect, useRef, useState } from "react";
import { Button, useToast } from "@humansignal/ui";
import { ApiContext } from "../../../providers/ApiProvider";

const STATUS = {
  good: "Good",
  warning: "Needs a look",
  problem: "Problem",
  unknown: "Not checked",
};

const size = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`
    : `${Math.ceil(bytes / 1024)} KB`;
const length = (seconds) => {
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

/**
 * A source connection's file check: whether its files load and play in the
 * browser, and which are heavy. Runs once by itself after a sync it has not
 * seen, and on request.
 */
export const StorageHealth = ({ storage, rootClass }) => {
  const api = useContext(ApiContext);
  const toast = useToast();
  const [report, setReport] = useState(storage.meta?.health ?? null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [open, setOpen] = useState(false);
  const asked = useRef(null);

  const check = async (byHand) => {
    setChecking(true);
    const res = await api.callApi("storageHealth", {
      params: { type: storage.type, pk: storage.id },
      errorFilter: () => true,
    });
    setChecking(false);
    if (res?.status) setReport(res);
    else if (byHand) toast?.show({ message: "The files could not be checked.", type: "error" });
  };

  useEffect(() => {
    setReport(storage.meta?.health ?? null);
  }, [storage.meta?.health?.checked_at]);

  // Once per sync: a sync newer than the last check is checked.
  useEffect(() => {
    const synced = storage.last_sync;
    if (!synced || storage.status !== "completed" || asked.current === synced) return;
    const checked = storage.meta?.health?.checked_at;
    if (checked && new Date(checked) >= new Date(synced)) return;
    asked.current = synced;
    check(false);
  }, [storage.last_sync, storage.status]);

  // Put the rule on the bucket, then show the new check.
  const apply = async () => {
    setApplying(true);
    const res = await api.callApi("storageHealthCors", {
      params: { type: storage.type, pk: storage.id },
      errorFilter: () => true,
    });
    setApplying(false);
    if (!res?.status) {
      return toast?.show({
        message: res?.response?.detail ?? res?.response?.validation_errors?.detail ?? "The rule could not be added.",
        type: "error",
      });
    }
    setReport(res);
    toast?.show({
      message: res.added
        ? "Rule added. Browsers can take a few minutes to see it."
        : "The bucket already allows this site. Browsers can take a few minutes to see a new rule.",
    });
  };

  const status = report?.status ?? "unknown";
  const findings = report?.findings ?? [];
  const copy = async (rule) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(rule, null, 2));
      toast?.show({ message: "CORS rule copied." });
    } catch (_) {
      toast?.show({ message: "Copying is not allowed here. Select the rule and copy it.", type: "error" });
    }
  };

  return (
    <div className={rootClass.elem("health").toClassName()} data-testid="storage-health">
      <div className={rootClass.elem("health-head").toClassName()}>
        <span
          className={rootClass
            .elem("health-status")
            .mod({ [status]: true })
            .toClassName()}
        >
          {STATUS[status]}
        </span>
        <span className={rootClass.elem("health-summary").toClassName()}>
          {checking
            ? "Checking the files…"
            : (report?.summary ?? "Sync the connection, then check how its files load.")}
        </span>
        {findings.length || report?.files?.length ? (
          <Button size="small" look="string" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? "Hide" : "Details"}
          </Button>
        ) : null}
        <Button
          size="small"
          look="outlined"
          onClick={() => check(true)}
          waiting={checking}
          aria-label="Check the files"
        >
          Check files
        </Button>
      </div>
      {open ? (
        <div className={rootClass.elem("health-body").toClassName()}>
          {findings.map((f) => (
            <div
              key={f.id}
              className={rootClass
                .elem("health-finding")
                .mod({ [f.level]: true })
                .toClassName()}
            >
              <strong>{f.title}</strong>
              <span>{f.detail}</span>
              <span>{f.fix}</span>
              {f.rule ? (
                <>
                  <pre>{JSON.stringify(f.rule, null, 2)}</pre>
                  <span className={rootClass.elem("health-actions").toClassName()}>
                    {f.can_apply ? (
                      <Button size="small" onClick={apply} waiting={applying} data-testid="storage-cors-apply">
                        Add the rule
                      </Button>
                    ) : null}
                    <Button size="small" look="outlined" onClick={() => copy(f.rule)}>
                      Copy the rule
                    </Button>
                  </span>
                </>
              ) : null}
            </div>
          ))}
          {report?.files?.length ? (
            <ul className={rootClass.elem("health-files").toClassName()}>
              {report.files.map((f) => (
                <li key={f.name}>
                  <span>{f.name}</span>
                  <em>
                    {[f.error, f.kind, f.size ? size(f.size) : null, f.duration ? length(f.duration) : null]
                      .filter(Boolean)
                      .join(", ")}
                  </em>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
